import { useState, useRef, useEffect } from "react";
import { CheckIcon, XIcon, PencilIcon } from "lucide-react";
import { cn } from "@util/tailwind";
import { useSnackbar } from "@hook/use-snackbar";

interface InlineEditableFieldProps {
    value: string;
    onSave: (newValue: string) => void;
    className?: string;
    type?: "text" | "email" | "tel";
    placeholder?: string;
    maxWidth?: string;
}

export default function InlineEditableField({
    value,
    onSave,
    className,
    type = "text",
    placeholder,
    maxWidth,
}: InlineEditableFieldProps) {
    const snackbar = useSnackbar();
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing]);

    const handleSave = () => {
        if (type === "email" && !editValue.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
            snackbar.showError("Please enter a valid email address.");
            return;
        }

        if (editValue.trim() !== value) {
            onSave(editValue.trim());
        }
        setIsEditing(false);
    };

    const handleCancel = () => {
        setEditValue(value);
        setIsEditing(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            handleSave();
        } else if (e.key === "Escape") {
            handleCancel();
        }
    };

    if (isEditing) {
        return (
            <div className="relative inline-flex items-center h-8 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-950 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                <div className="flex items-center gap-2 w-full" style={maxWidth ? { maxWidth } : undefined}>
                    <input
                        ref={inputRef}
                        type={type}
                        value={editValue}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onBlur={handleCancel}
                        className="flex-1 h-full px-2 text-sm bg-transparent border-0 outline-none text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500"
                        placeholder={placeholder}
                    />
                    <button
                        onMouseDown={(e) => {
                            e.preventDefault();
                            handleSave();
                        }}
                        className="flex-shrink-0 p-1 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
                    >
                        <CheckIcon className="w-4 h-4" />
                    </button>
                    <button
                        onMouseDown={(e) => {
                            e.preventDefault();
                            handleCancel();
                        }}
                        className="flex-shrink-0 p-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                    >
                        <XIcon className="w-4 h-4" />
                    </button>
                </div>
            </div>
        );
    }

    // Empty value: whole field acts as the "+ Add" affordance, so the entire
    // surface enters edit mode (and stops propagation so the row click doesn't
    // also open the profile drawer).
    if (!value) {
        return (
            <div
                className={cn(
                    "group flex items-center gap-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 rounded px-2 py-1 -mx-2 -my-1 w-full",
                    className
                )}
                onClick={(e) => {
                    e.stopPropagation();
                    setIsEditing(true);
                }}
            >
                <span className="text-sm truncate text-blue-600 dark:text-blue-400">{placeholder || "—"}</span>
            </div>
        );
    }

    // Non-empty value: clicking the text bubbles up to the row (which opens
    // the profile drawer). Only the pencil icon enters edit mode. Pencil is
    // hidden until row/cell hover.
    return (
        <div
            className={cn(
                "group inline-flex items-center gap-1 rounded px-2 py-1 -mx-2 -my-1 max-w-full",
                className
            )}
        >
            <span className="text-sm truncate text-gray-900 dark:text-gray-100">{value}</span>
            <button
                type="button"
                aria-label="Edit"
                onClick={(e) => {
                    e.stopPropagation();
                    setIsEditing(true);
                }}
                className="flex-shrink-0 p-0.5 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
            >
                <PencilIcon className="w-3.5 h-3.5" />
            </button>
        </div>
    );
}